// src/pages/Login.tsx
import React, { useState, useEffect } from "react";
import { FcGoogle } from "react-icons/fc";
// import { FaFacebook } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import useAuthStore from "@/stores/user/authStore";
import waytora_logo from "@/assets/logos/internal/waytora_blue_text_logo.svg";
import { toast } from "@/hooks/use-toast";

const Login: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const {
    user,
    signIn,
    signInWithGoogle,
    // signInWithFacebook,
    isLoading,
    isSocialLoading,
    anonymousUser,
  } = useAuthStore();

  const navigate = useNavigate();

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  useEffect(() => {
    if (user && user.first_name && !anonymousUser) {
      setTimeout(() => {
        setEmail("");
        setPassword("");
        navigate("/");
      }, 1000);
    }
  }, [user, navigate, anonymousUser]);

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError(false);

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError(true);
      toast({
        variant: "destructive",
        title: "Invalid Email",
        description: "Please enter a valid email address",
      });
      return;
    }

    if (!password || !email) {
      toast({
        variant: "destructive",
        title: "Missing Fields",
        description: "Please fill in all fields",
      });
      return;
    }

    try {
      const result = await signIn(email, password);
      if (result) {
        navigate("/auth/callback");
      }
    } catch (error) {
      console.error("Login error:", error);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      await signInWithGoogle();
    } catch (error) {
      console.error("Error signing in with Google", error);
    }
  };

  // const handleFacebookSignIn = async () => {
  //   try {
  //     await signInWithFacebook();
  //   } catch (error) {
  //     console.error("Error signing in with Facebook", error);
  //   }
  // };

  return (
    <section className="bg-white overflow-hidden flex flex-col min-h-dvh justify-center relative">
      {/* Overlay for social loading blur effect */}
      <div
        className={`
          absolute inset-0 z-50 
          bg-white/30 
          transition-opacity duration-[1500ms] ease-in-out
          ${
            isSocialLoading
              ? "opacity-100 backdrop-blur-xl pointer-events-auto"
              : "opacity-0 pointer-events-none"
          }
        `}
      />
      <div className="flex flex-row items-center justify-center transform -translate-y-10">
        <div className="w-full md:w-1/2 px-4">
          <div className="container px-4 mx-auto">
            <div className="flex flex-wrap">
              <div className="w-full">
                <div className="md:max-w-lg mx-auto pt-16 md:pb-32">
                  <div className="mb-6">
                    <img
                      src={waytora_logo}
                      alt="Waytora Logo"
                      className="w-[40%]"
                    />
                  </div>
                  <h2 className="text-neutral-800 text-[75px] tracking-tight font-bold leading-[70px]">
                    Let's get you out there and going again.
                  </h2>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="w-1/2">
          <div className="p-4 mt-24 flex flex-col justify-center h-full">
            <div className="md:max-w-xl px-16 text-[15px]">
              <form className="relative w-full" onSubmit={handleEmailLogin}>
                <div className="w-full flex flex-col justify-between items-center mb-4">
                  <label className="w-full mb-4">
                    <p className="mb-2 text-neutral-900 font-semibold leading-normal">
                      Email Address
                    </p>
                    <input
                      className={`px-4 py-3.5 w-full text-neutral-700 font-normal placeholder-neutral-400 bg-white outline-none border ${
                        emailError
                          ? "border-red-500 ring-red-500"
                          : "border-neutral-300"
                      } rounded-lg focus:ring-2 focus:outline-none focus:border-transparent focus:ring-indigo-600`}
                      type="text"
                      placeholder="john.doe@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </label>
                  <label className="w-full mb-5 relative">
                    <p className="mb-2 text-neutral-900 font-semibold leading-normal">
                      Password
                    </p>
                    <input
                      className={`px-4 py-3.5 w-full text-neutral-700 font-normal placeholder-neutral-400 bg-white outline-none border
                      rounded-lg focus:ring-2 focus:outline-none focus:border-transparent focus:ring-indigo-600`}
                      type={showPassword ? "text" : "password"}
                      placeholder="•••••••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <button
                      type="button"
                      className="absolute w-8 h-8 rounded-full right-3 top-[42px] text-neutral-700 hover:bg-neutral-200/50 focus:outline-none"
                      onClick={togglePasswordVisibility}
                    >
                      {showPassword ? (
                        <i className="fa-solid fa-eye"></i>
                      ) : (
                        <i className="fa-solid fa-eye-slash"></i>
                      )}
                    </button>
                  </label>
                </div>
                <button
                  className="py-3 px-6 w-full text-white font-semibold border border-indigo-700 rounded-lg shadow-4xl focus:ring-0 focus:border-transparent focus:outline-none bg-indigo-600 hover:bg-indigo-700 transition ease-in-out duration-200"
                  type="submit"
                >
                  {isLoading ? (
                    <i className="fa-solid fa-circle-notch fa-spin"></i>
                  ) : (
                    "Sign In"
                  )}
                </button>
              </form>
              <p className="my-4 text-sm text-neutral-500 font-light text-center">
                Or
              </p>
              <div className="w-full flex flex-col justify-center items-center space-y-4">
                <div className="w-full flex flex-row justify-center items-center space-x-4">
                  <button
                    onClick={handleGoogleSignIn}
                    className="w-full flex justify-center items-center py-3 bg-white ring-1 ring-neutral-200 hover:bg-neutral-100 hover:shadow-lg rounded-lg transition ease-in-out duration-300 gap-3"
                    disabled={isSocialLoading}
                  >
                    <FcGoogle className="w-6 h-6" />
                    <span className="text-sm font-normal text-neutral-700">
                      Continue with Google
                    </span>
                  </button>
                  {/* <button
                    onClick={handleFacebookSignIn}
                    className="flex justify-center items-center w-14 h-14 bg-[#316FF6] hover:bg-blue-700 ring-1 ring-[#316FF6] hover:shadow-lg rounded-lg transition ease-in-out duration-300"
                  >
                    <FaFacebook className="text-[28px] text-white" />
                  </button> */}
                </div>
                <p className="text-neutral-500 text-sm font-light leading-normal">
                  Don't have an account?{" "}
                  <Link
                    to="/signup"
                    className="text-indigo-600 hover:text-indigo-800"
                  >
                    Sign Up
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Login;
